import { Component, OnInit, inject } from '@angular/core';
import {TranslatePipe, TranslateService} from "@ngx-translate/core";
import {Observable, Subject} from "rxjs";
import {take, tap} from "rxjs/operators";
import {EmployeeView, ProblemInstanceService, RoutesResult, RoutesService} from "planner-shared";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {TableModule} from "../../../../../_shared/table/table.module";
import {NotificationService, SharedModule} from "@shared";
import {RouteTaskService} from "../../tasks/services/route-task.service";
import {RouteTaskView} from "../../tasks/route-task-view.model";
import {RouteVisualizationService} from "../../tasks/services/route-visualization.service";
import {TaskColorService} from "../../../shared/services/task-color/task-color.service";
import {SwapShiftService} from "../../../shared/services/swap-shift/swap-shift.service";
import {SwapShift} from "../../../shared/models/swap-shift-model";
import {FeatureFlagService, FeatureFlags} from "@core";

@UntilDestroy()
@Component({
    selector: 'app-employee-swap-modal',
    imports: [
        TranslatePipe,
        TableModule,
        SharedModule
    ],
    templateUrl: './employee-swap-modal.component.html',
    styleUrl: './employee-swap-modal.component.scss'
})
export class EmployeeSwapModalComponent implements OnInit {
    private readonly problemInstanceService = inject(ProblemInstanceService);
    private readonly routesService = inject(RoutesService);
    private readonly routeTaskService = inject(RouteTaskService);
    private readonly routeVisualizationService = inject(RouteVisualizationService);
    private readonly taskColorService = inject(TaskColorService);
    private readonly swapShiftService = inject(SwapShiftService);
    private readonly featureFlagService = inject(FeatureFlagService);
    private readonly notificationService = inject(NotificationService);
    private readonly translateService = inject(TranslateService);

    public selectedEmployees: EmployeeView[];

    public cancel$: Subject<void>;
    public confirm$: Subject<RoutesResult>;

    public isUpdating = false;
    public employeeTasksMap: RouteTaskView[][] = [];
    public selectedTasksMap: Map<number, Set<string>> = new Map();

    public allowTaskSelection = false;
    public swapShiftsTaskSelectionFeatureEnabled$: Observable<boolean>;

    private swapShiftsTaskSelectionFeatureEnabled = false;

    public set parameter(selectedEmployees: Set<EmployeeView>) {
        this.selectedEmployees = Array.from(selectedEmployees);
        this.initializeRoute();
    }

    ngOnInit() {
        this.swapShiftsTaskSelectionFeatureEnabled$
            = this.featureFlagService.isEnabled(FeatureFlags.SwapWorklistsTaskSelection);

        this.swapShiftsTaskSelectionFeatureEnabled$
            .pipe(
                take(1),
                untilDestroyed(this),
                tap(flagValue => {
                    this.swapShiftsTaskSelectionFeatureEnabled = flagValue;
                    if (!flagValue) {
                        this.allowTaskSelection = false;
                        this.selectedTasksMap.clear();
                    }
                })
            ).subscribe();
    }

    constructor() {
        this.cancel$ = new Subject();
        this.confirm$ = new Subject();
    }


    public dismissModal(): void {
        this.cancel$.next();
    }

    public getRideSharingCompanionsName(routeTask: RouteTaskView): string {
        return this.routeVisualizationService.getRideSharingCompanionsName(routeTask);
    }

    public isTaskSelected(employeeIndex: number, taskId: string): boolean {
        return this.selectedTasksMap.get(employeeIndex)?.has(taskId) ?? false;
    }

    public toggleTaskSelection(employeeIndex: number, taskId: string, isSelected: boolean): void {
        const task = this.employeeTasksMap[employeeIndex]?.find(t => t.routePlannerTaskId === taskId);
        if (task?.isSynced) {
            return;
        }
        if (!this.selectedTasksMap.has(employeeIndex)) {
            this.selectedTasksMap.set(employeeIndex, new Set());
        }

        const taskSet = this.selectedTasksMap.get(employeeIndex);
        if (isSelected) {
            taskSet.add(taskId);
        } else {
            taskSet.delete(taskId);
        }
    }

    public onSwapModeChanged(allowTaskSelection: boolean): void {
        this.allowTaskSelection = allowTaskSelection;
        if (!allowTaskSelection) {
            this.selectedTasksMap.clear();
        }
    }

    public performSwap(): void {
        const includeSelectedTasksInRequest = this.swapShiftsTaskSelectionFeatureEnabled && this.allowTaskSelection;
        const swapShift = {
            swapEmployeeShiftId: this.selectedEmployees[0].workHourId,
            toSwapEmployeeShiftId: this.selectedEmployees[1].workHourId,
            swapWorkShiftType: this.selectedEmployees[0].shiftType,
            swapEmployeeTaskIds: includeSelectedTasksInRequest ? this.getSelectedTaskIds(0) : undefined,
            toSwapEmployeeTaskIds: includeSelectedTasksInRequest ? this.getSelectedTaskIds(1) : undefined
        } as SwapShift;
        this.isUpdating = true;
        this.swapShiftService.swapShift(swapShift).subscribe({
            next: routeResults => {
                this.routesService.setRoutes()
                    .pipe(untilDestroyed(this))
                    .subscribe(() => {
                        const toasterMsg = this.translateService.instant("SWAP_SUCCESSFUL_TOASTER",
                            {
                                firstEmployeeName: this.selectedEmployees[0].employeeName,
                                secondEmployeeName: this.selectedEmployees[1].employeeName
                            });
                        this.notificationService.success(toasterMsg);
                        this.routesService.updateLocalRoutes(routeResults);
                        this.confirm$.next(routeResults);
                    });
            },
            error: () => {
                this.dismissModal();
            }
        });
    }

    private getSelectedTaskIds(employeeIndex: number): string[] | undefined {
        const selectedTasks = this.selectedTasksMap.get(employeeIndex);
        if (!selectedTasks || selectedTasks.size === 0) {
            return undefined;
        }
        return Array.from(selectedTasks);
    }

    private initializeRoute(): void {
        this.problemInstanceService.getProblemInstance()
            .pipe(untilDestroyed(this))
            .subscribe(problemInstance => {
                this.routesService.getLocalRoutes(problemInstance.problemInstance.jobId)
                    .subscribe(routes => {
                        this.routeTaskService.mapToRouteTasks(routes.routes.tasks).subscribe(mappedRoute => {
                            if (mappedRoute.some(task => task.rideSharingConfig)) {
                                mappedRoute = this.routeVisualizationService
                                    .loadRideSharingTasksDescription(mappedRoute, false);
                            }
                            this.selectedEmployees.forEach(employee => {
                                this.employeeTasksMap
                                    .push(mappedRoute.filter(task => task.workHourId === employee.workHourId));
                                this.taskColorService.getTaskColors().forEach(coloredTask => {
                                    const uncoloredTask =
                                        mappedRoute.find(routeTask => routeTask.taskId === coloredTask.taskId);
                                    if (uncoloredTask) {
                                        uncoloredTask.color = coloredTask.color;
                                    }
                                });
                            });
                        });
                    });
            });
    }
}
